import { useEffect, useState } from "react";
import api from "../lib/api";
import { Badge } from "./ui/badge";
import {
    ShieldCheck,
    ShieldAlert,
    FileCheck2,
    FileClock,
    FileSignature,
    Loader2,
    RefreshCw,
} from "lucide-react";

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

/**
 * HR Compliance Panel — shows at-a-glance the consent + delivery status
 * for a single employee. Useful for labour-inspection audits and contracts.
 */
export const ComplianceHRPanel = ({ employee }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchData = async () => {
        try {
            const { data } = await api.get(`/delivery/employees/${employee.id}/compliance`);
            setData(data);
        } catch (e) {
            setData(null);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employee?.id]);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchData();
    };

    if (loading) {
        return (
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="compliance-panel">
                <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="compliance-panel">
                <p className="text-sm text-slate-500">Dati di conformità non disponibili.</p>
            </div>
        );
    }

    const { consent, documents, last_download_at } = data;
    const consentGiven = consent?.given;
    const total = documents?.total || 0;
    const downloaded = documents?.downloaded || 0;
    const acknowledged = documents?.acknowledged || 0;
    const pendingDownload = documents?.pending_download || 0;
    const requiresAck = documents?.requires_ack || 0;
    const pendingAck = documents?.pending_ack || 0;

    const downloadRate = total > 0 ? Math.round((downloaded / total) * 100) : 0;

    return (
        <div
            className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
            data-testid="compliance-panel"
        >
            <div className="mb-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="font-display text-base font-bold text-[#376DB3]">Conformità HR</h3>
                        <p className="text-xs text-slate-500">
                            Consegna telematica · informativa · presa visione
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-50"
                    title="Aggiorna"
                    data-testid="compliance-refresh-btn"
                >
                    <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                </button>
            </div>

            {/* Consent banner */}
            <div
                className={`mb-4 flex items-start gap-3 rounded-md border p-3 ${consentGiven ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}
                data-testid="compliance-consent-banner"
            >
                {consentGiven ? (
                    <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                ) : (
                    <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                )}
                <div className="flex-1 text-sm">
                    {consentGiven ? (
                        <>
                            <div className="font-medium text-emerald-900" data-testid="compliance-consent-status">
                                Informativa accettata
                            </div>
                            <div className="text-xs text-emerald-700">
                                il <b>{fmtDateTime(consent.given_at)}</b>
                                {consent.ip && <span> · IP <span className="font-mono">{consent.ip}</span></span>}
                                {consent.version && <span> · versione {consent.version}</span>}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="font-medium text-amber-900" data-testid="compliance-consent-status">
                                Informativa non ancora accettata
                            </div>
                            <div className="text-xs text-amber-700">
                                Il dipendente deve accettare l'informativa al primo accesso al portale.
                                {employee?.can_login === false && " (Le credenziali di accesso non sono ancora state impostate.)"}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* KPI row */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Stat
                    testid="compliance-stat-total"
                    icon={FileCheck2}
                    label="Documenti consegnati"
                    value={total}
                    sub={total > 0 ? `${downloadRate}% scaricati` : "Nessun documento"}
                    tone="blue"
                />
                <Stat
                    testid="compliance-stat-downloaded"
                    icon={FileCheck2}
                    label="Scaricati dal dipendente"
                    value={downloaded}
                    sub={total > 0 ? `${downloaded} su ${total}` : "—"}
                    tone="emerald"
                />
                <Stat
                    testid="compliance-stat-acknowledged"
                    icon={FileSignature}
                    label="Presa visione confermata"
                    value={acknowledged}
                    sub={requiresAck > 0 ? `${acknowledged} su ${requiresAck} richiesti` : "Nessuno richiede ack"}
                    tone="violet"
                />
                <Stat
                    testid="compliance-stat-pending"
                    icon={FileClock}
                    label="In attesa di scaricare"
                    value={pendingDownload}
                    sub={pendingAck > 0 ? `di cui ${pendingAck} con presa visione pendente` : "Nessuna pendenza"}
                    tone={pendingDownload > 0 ? "amber" : "slate"}
                />
            </div>

            {/* Footer info */}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                <div>
                    Ultimo download:{" "}
                    <span className="font-medium text-slate-700" data-testid="compliance-last-download">
                        {last_download_at ? fmtDateTime(last_download_at) : "mai"}
                    </span>
                </div>
                {pendingAck > 0 && (
                    <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 text-amber-700">
                        <FileClock className="h-3 w-3" />
                        {pendingAck} presa visione in sospeso
                    </Badge>
                )}
            </div>
        </div>
    );
};

const TONE_CLASSES = {
    blue: "bg-blue-50 text-blue-900 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
    violet: "bg-violet-50 text-violet-900 border-violet-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
    slate: "bg-slate-50 text-slate-800 border-slate-200",
};

const Stat = ({ icon: Icon, label, value, sub, tone = "slate", testid }) => (
    <div className={`rounded-md border p-3 ${TONE_CLASSES[tone] || TONE_CLASSES.slate}`} data-testid={testid}>
        <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{label}</div>
            <Icon className="h-4 w-4 opacity-60" />
        </div>
        <div className="mt-1 font-mono-numbers text-2xl font-bold">{value}</div>
        <div className="text-xs opacity-75">{sub}</div>
    </div>
);
